import { useMemo, useRef } from 'react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import {
  ApiException,
  ApiFormattedException,
  ErrorWithMessage,
} from '@app/api';
import * as Sentry from '@sentry/react';

export const useToast = () => {
  const { t } = useTranslation('common');
  const exception = (e?: unknown) => {
    if (e instanceof ApiFormattedException && e.getTranslatedMessage()) {
      toast.error(e.getTranslatedMessage());
    } else if (e instanceof ErrorWithMessage && e.translatedMessage) {
      toast.error(e.translatedMessage);
    } else {
      toast.error(t('toast.genericError') as string);
      if (e instanceof Error) {
        if (e instanceof ApiException && (e.response?.status || 0) < 500) {
          return;
        }

        Sentry.captureException(e);
      }
    }
  };

  const saved = () => {
    toast.success(t('toast.saved') as string);
  };

  const success = (messageTranslationKey: string) => {
    toast.success(t(messageTranslationKey) as string);
  };

  const invalidForm = () => {
    toast.error(t('toast.invalidForm') as string);
  };

  const error = (e: unknown) => {
    if (typeof e === 'string') {
      toast.error(e);
    }
  };

  const ref = useRef({
    ...toast,
    exception,
    saved,
    success,
    invalidForm,
    error,
  });

  return ref.current;
};

export const useToastOptions = (options?: {
  onSuccess?: () => any;
  onError?: (e: Error) => any;
}) => {
  const toast = useToast();
  return useMemo(
    () => ({
      onSuccess: () => {
        toast.saved();
        options?.onSuccess?.();
      },
      onError: (e: any) => {
        toast.exception(e);
        options?.onError?.(e);
      },
    }),
    [toast, options],
  );
};
